import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneCall, Loader2, Briefcase, Building, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const HeroCallbackForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (window.trackLeadConversion) {
        window.trackLeadConversion('hero_callback', 'Hero Callback Form');
      }

      await axios.post(`${API_URL}/api/enquiry`, {
        full_name: formData.name,
        mobile: formData.phone,
        email: "",
        course: "General Enquiry",
        message: `Callback request from Hero section - City: ${formData.city}`
      });

      toast.success("We'll call you back within 30 minutes!");
      const scrollY = window.scrollY;
      navigate(`/thank-you?returnTo=/&scrollY=${scrollY}`);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const trustBadges = [
    { icon: Briefcase, text: "100% Job Placement", subtext: "Guaranteed" },
    { icon: Building, text: "100+ Hotels", subtext: "Partner Network" },
    { icon: Users, text: "Limited Seats", subtext: "2026 Batch" }
  ];

  return (
    <section className="py-8 bg-gradient-to-r from-[hsl(345,60%,25%)] to-[hsl(345,60%,30%)]" id="hero-callback">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Trust Badges - Mobile: Stack vertically, Desktop: Row */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
            {trustBadges.map((badge, index) => (
              <div 
                key={index} 
                className="text-center"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-1 md:mb-2">
                  <badge.icon className="w-5 h-5 md:w-6 md:h-6 text-[hsl(43,74%,60%)]" />
                </div>
                <p className="text-white font-semibold text-xs md:text-sm">{badge.text}</p>
                <p className="text-white/70 text-[10px] md:text-xs">{badge.subtext}</p>
              </div>
            ))}
          </div>

          {/* Form Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/20">
            <div className="text-center mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center justify-center gap-2">
                <PhoneCall className="w-5 h-5 md:w-6 md:h-6 text-[hsl(43,74%,60%)]" />
                Request a FREE Callback
              </h2>
              <p className="text-white/80 text-sm mt-1">Get expert counselling within 30 minutes</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Mobile: Stack vertically, Desktop: Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-white/90 border-0 h-11 md:h-12 text-gray-800 placeholder:text-gray-500"
                  data-testid="hero-callback-name"
                />
                <Input
                  placeholder="Mobile Number *"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  pattern="[0-9]{10}"
                  className="bg-white/90 border-0 h-11 md:h-12 text-gray-800 placeholder:text-gray-500"
                  data-testid="hero-callback-phone"
                />
                <Input
                  placeholder="City *"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="bg-white/90 border-0 h-11 md:h-12 text-gray-800 placeholder:text-gray-500"
                  data-testid="hero-callback-city"
                />
              </div>
              
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(43,74%,49%)] hover:bg-[hsl(43,74%,45%)] text-[hsl(345,60%,20%)] font-bold h-12 md:h-14 text-base md:text-lg shadow-lg"
                data-testid="hero-callback-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <PhoneCall className="w-5 h-5 mr-2" />
                    Get FREE Callback Now
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-white/60 text-xs mt-3">
              🔒 Your information is secure and will not be shared
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCallbackForm;
